import { cn } from "@drpcrd/utils/shared";
import { Nullable } from "@drpcrd/utils";
import { formatCdnImg } from "@drpcrd/utils/formatters";
import { Building } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { Avatar, AvatarFallback, AvatarImage } from "./avatar";

const companyAvatarVariants = cva("", {
  variants: {
    size: {
      xs: "h-3 w-3",
      sm: "h-4 w-4",
      md: "h-5 w-5",
      lg: "h-9 w-9",
    },
  },
  defaultVariants: { size: "md" },
});

type Props = VariantProps<typeof companyAvatarVariants> & {
  logoSrc?: Nullable<string>;
  name?: string;
  className?: string;
};

/**
 * Avatar for a company/workspace identity (e.g. a talent agency or brand),
 * showing its logo with a building icon fallback. Distinct from
 * `CreatorAvatar`, which represents an individual creator.
 */
export const CompanyAvatar = ({ logoSrc, name, size = "md", className }: Props) => {
  // `Campaigns.logo` defaults to "" rather than null — treat that as no
  // logo, otherwise `formatCdnImg` hands the image the bare CDN host.
  // Already-absolute URLs (e.g. a Clerk org image) are used as-is.
  const src = !logoSrc
    ? undefined
    : logoSrc.startsWith("http")
      ? logoSrc
      : formatCdnImg(logoSrc);

  return (
    <Avatar className={cn(companyAvatarVariants({ size }), className)}>
      <AvatarImage src={src} alt={name ?? ""} />
      <AvatarFallback className="bg-primary-50">
        <Building className="text-primary-500 h-[60%] w-[60%]" />
      </AvatarFallback>
    </Avatar>
  );
};
